
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  fetchAllRevsForProject,
  fetchExtractPnid,
  handleExtractLineListOnly,
  postPipingRev,
  fetchPipingRev,
  updatePipingRev,
  getPipingPlantProjects,
  getAllData,
  getProjectById,
} from "./api";
import { useParams } from "react-router-dom";
import { useGeneralContext } from "../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import {
  isPlainObject,
  createEmptyDataSheetsStore,
  createEmptyPnidRevision,
  normalizeDataSheetsStore,
  normalizePnidRevisionData,
  buildNormalizedPnidData,
} from "./pnidNormalizers";
import { mergeBulkDataWithDerivation, mergeBulkData } from "./MainTabs/PnID/BulkList/bulkListHelpers";
import {
  buildDataSheetForSourceRow,
  buildRowDataSheet,
  consolidateDataSheets,
} from "./MainTabs/PnID/DataSheets/dataSheetHelpers";
import { secondAxios } from "../../../../api/axios";

const PIPING_TYPE = "piping";

function migrateTagNumbersList(value) {
  if (!Array.isArray(value)) return [];
  if (value.length === 0) return [];

  if (typeof value[0] === "object" && value[0] !== null) {
    return value
      .filter((g) => g && typeof g === "object")
      .map((g) => ({
        lineNumber: String(g.lineNumber ?? "").trim(),
        tagNumbers: Array.isArray(g.tagNumbers)
          ? g.tagNumbers.map((t) => String(t ?? "").trim()).filter(Boolean)
          : [],
      }));
  }

  const tagNumbers = value.map((t) => String(t ?? "").trim()).filter(Boolean);
  return tagNumbers.length ? [{ lineNumber: "", tagNumbers }] : [];
}

/**
 * @typedef {Object} DrawingFile
 * @property {string} path
 * @property {string} name
 * @property {number} size
 * @property {string} modified
 */

/**
 * @typedef {Object} PipingContextValue
 * @property {*} specTable
 * @property {Function} setSpecTable
 * @property {*} fullSpecTable
 * @property {Function} setFullSpecTable
 * @property {*} partLibTable
 * @property {Function} setPartLibTable
 * @property {*} estimationTable
 * @property {Function} setEstimationTable
 * @property {*} trimListTable
 * @property {Function} setTrimListTable
 * @property {Object} allTables
 * @property {Function} initializeAllTables
 * @property {{label: string, spec: string, rev: string | number}[]} selectedSpecs
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} addSlectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} removeSelectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => Object} getSelectedSpecTable
 * @property {Function} savePipingData
 * @property {Function} setSelectedSpecs
 * @property {(pnidPath: string) => Promise<void>} extractPnid
 * @property {boolean} isFetchingPnid
 * @property {DrawingFile[]} pnidDrawings
 * @property {Dispatch<SetStateAction<DrawingFile[]>>} setPnidDrawings
 */

/** @type {React.Context<PipingContextValue | undefined>} */
const PipingContext = createContext(undefined);

export const PipingProvider = ({ children }) => {
  const {
    selectedModelingRevision,
    setSelectedModelingRevision,
    fastenersTable,
    setFastenersTable,
    modelingData,
    setModelingData,
  } = useGeneralContext();
  // const [UID_array, setUID_array] = useState([]);
  const [allTables, setAllTables] = useState({});
  const [specTable, setSpecTable] = useState([]);
  const [fullSpecTable, setFullSpecTable] = useState([]); // NEW: keeps duplicates for searching
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [partLibTable, setPartLibTable] = useState({});
  const [estimationTable, setEstimationTable] = useState({});

  const [pnidData, setPnidData] = useState({});
  const [selectedPnidRevision, setSelectedPnidRevision] = useState("");
  const [pnidProjectPath, setPnidProjectPath] = useState("");
  const [pnidRevisionFolders, setPnidRevisionFolders] = useState([]);
  const [pnidFolderTree, setPnidFolderTree] = useState(null);

  const [validationObject, setValidationObject] = useState({
    left: null,
    right: null,
  });

  const FRONTEND_UID_ARRAY = [
    "B06X3A-3",
    "B06X3A-4",
    "B06X3A-5",
    "UID-001",
    "UID-002",
    "UID-003",
  ];
  const [revision, setRevision] = useState(undefined);
  const [isFetchingPnid, setIsFetchingPnid] = useState(false);
  const [pnidDrawings, setPnidDrawings] = useState([]);
  const [pdcAttachItems, setPdcAttachItems] = useState([]);
  const [allUiData, setAllUiData] = useState({});
  const [pageViews, setPageViews] = useState({});
  const [pages, setPages] = useState([]);
  const [projectPath, setProjectPath] = useState(null);
  const [hookupsTable, setHookupsTable] = useState([]);
  const [tagNumbersList, setTagNumbersList] = useState([]);
  const [selectedLineNumber, setSelectedLineNumber] = useState("");
  const [allCombinationTable, setAllCombinationTable] = useState([]);
  const [UID_array, setUID_array] = useState(FRONTEND_UID_ARRAY);
  const SelectedParts = useMemo(() => {
    const byTab = {};
    allCombinationTable.forEach((row) => {
      if (row.select && row.TabName) {
        if (!byTab[row.TabName]) byTab[row.TabName] = [];
        byTab[row.TabName].push(row);
      }
    });
    return byTab;
  }, [allCombinationTable]);
 const[selectedProjectId, setSelectedProjectId] = useState(null);
  const [totalSummary, setTotalSummary] = useState([
    { name: "Total Materials Cost", value: "", unit: "$", rate: "", total: "" },
    { name: "Total Man Hours", value: "", unit: "hr", rate: "", total: "" },
    { name: "Galvanized Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Painting Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Fire Proof Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Miscellaneous", value: "", unit: "LS", rate: "1", total: "" },
  ]);

  const getCurrentRevisionData = useCallback(() => {
    if (!selectedPnidRevision) {
      return createEmptyPnidRevision();
    }

    return normalizePnidRevisionData(pnidData[selectedPnidRevision]);
  }, [pnidData, selectedPnidRevision]);

  const trimListTable = getCurrentRevisionData().trimListTable;
  const bulkListTable = getCurrentRevisionData().bulkListTable;
  const valveListTable = getCurrentRevisionData().valveListTable;
  const instrumentIndexTable = getCurrentRevisionData().instrumentIndexTable;
  const lineListTable = getCurrentRevisionData().lineListTable;
  const equipmentListTable = getCurrentRevisionData().equipmentListTable;
  const titleBlockData = getCurrentRevisionData().titleBlockData;
  const dataSheetsStore = getCurrentRevisionData().dataSheetsStore;

  const initializeRevisionIfNeeded = useCallback((revisionKey) => {
    if (!revisionKey) return;

    setPnidData((prev) => {
      if (prev[revisionKey]) return prev;
      return {
        ...prev,
        [revisionKey]: createEmptyPnidRevision(),
      };
    });
  }, []);

  const clearSelectedPnidRevision = useCallback(() => {
    if (!selectedPnidRevision) return;

    setPnidData((prev) => ({
      ...prev,
      [selectedPnidRevision]: createEmptyPnidRevision(),
    }));
  }, [selectedPnidRevision]);

  const setTrimListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          trimListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.trimListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setBulkListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          bulkListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.bulkListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setValveListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          valveListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.valveListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setInstrumentIndexTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          instrumentIndexTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.instrumentIndexTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setLineListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          lineListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.lineListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setEquipmentListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          equipmentListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.equipmentListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setDataSheetsStore = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          dataSheetsStore:
            typeof newData === "function"
              ? newData(
                prev[selectedPnidRevision]?.dataSheetsStore ||
                createEmptyPnidRevision().dataSheetsStore,
              )
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setTitleBlockData = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          titleBlockData:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.titleBlockData || {})
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  // ────────────────────────────────────────────────────────────────────────────

  function processSpecData(rows) {
    // Remove duplicates
    const seen = new Map();
    for (const row of rows) {
      const key = `${row.Spec}|${row.Components}|${row.Size}|${row.Material}|${row.Ends}|${row["Schedule/Rating"]}|${row.Rev}`;
      if (!seen.has(key)) {
        seen.set(key, row);
      }
    }
    const uniqueRows = Array.from(seen.values());

    // Sort alphabetically by Components, then by Size
    return uniqueRows.sort((a, b) => {
      const compCompare = (a.Components || "").localeCompare(
        b.Components || "",
      );
      if (compCompare !== 0) return compCompare;

      // Natural sort for sizes
      const sizeA = parseFloat(a.Size) || 0;
      const sizeB = parseFloat(b.Size) || 0;
      if (sizeA !== sizeB) return sizeA - sizeB;

      return (a.Size || "").localeCompare(b.Size || "");
    });
  }

  const [clientName, setClientName] = useState("");

  const initializeAllTables = useCallback(async (clientNameArg = "DEMO") => {
  const safeClientName = String(clientNameArg || "DEMO")
    .trim()
    .toLowerCase();

  // 1. Get all piping_spec_ table names
  const specTablesResponse = await secondAxios.post(`/sqlite-db/query`, {
    dbName: "piping",
    sql: `
      SELECT name
      FROM sqlite_master
      WHERE type = 'table'
        AND name GLOB 'piping_spec_*'
      ORDER BY name
    `,
  });

  // Adjust this depending on your backend response shape
  const specTableRows = Array.isArray(specTablesResponse.data)
    ? specTablesResponse.data
    : specTablesResponse.data?.data || [];

  const specTableKeys = specTableRows.map((row) => row.name);

  const allSpecData = [];

  // 2. Load data from every spec table
  for (const specKey of specTableKeys) {
    const safeTableName = specKey.replace(/"/g, '""');

    const tableDataResponse = await secondAxios.post(`/sqlite-db/query`, {
      dbName: "piping",
      sql: `SELECT * FROM "${safeTableName}"`,
    });

    const tableData = Array.isArray(tableDataResponse.data)
      ? tableDataResponse.data
      : tableDataResponse.data?.data || [];

    allSpecData.push(...tableData);
  }

  // 3. Save full spec data
  setFullSpecTable([...allSpecData]);

  // 4. Process unique spec data
  const uniqueSpecData = processSpecData(allSpecData);
  setSpecTable(uniqueSpecData);
}, []);

  useEffect(() => {
    if (!selectedProjectId) return;

    async function fetchProject() {
      try {
        const base = await getProjectById(selectedProjectId);
        const nextClientName = String(base?.plant?.clientName || "DEMO").trim();

        // console.log("[PipingContext] Project clientName:", nextClientName);
        setClientName(nextClientName);
      } catch (error) {
        console.error("Failed to fetch project clientName:", error);
        setClientName("DEMO");
      }
    }

    fetchProject();
  }, [selectedProjectId]);

  // Then load tables after clientName is available
  useEffect(() => {
    if (!selectedProjectId) return;
    if (!clientName) return;

    initializeAllTables(clientName);
  }, [selectedProjectId, clientName, initializeAllTables]);

  function addSlectedSpec(newSelc) {
    setSelectedSpecs((prev) => {
      const exists = prev.some((item) => item.label === newSelc.label);
      return exists ? prev : [...prev, newSelc];
    });
  }

  function removeSelectedSpec(toRemove) {
    setSelectedSpecs((prev) =>
      prev.filter((item) => item.label !== toRemove.label),
    );
  }

  async function getRevisionsFromPath(path) {
    if (!path) return [];

    const fn = window.electron?.freecad?.getRevisionsFromPath;
    if (typeof fn === "function") {
      const revs = await fn(path);
      return revs || [];
    }

    return [];
  }

  const getSelectedSpecTable = useCallback(
    (selc) => {
      if (!selc) return [];
      if (!specTable || !Array.isArray(specTable)) return [];

      return specTable.filter(
        (item) =>
          item.Spec === selc.spec &&
          item.Rev?.toString() === selc.rev?.toString(),
      );
    },
    [specTable],
  );

  async function loadPipingData(projectIdValue) {
    const revisions = await fetchAllRevsForProject(projectIdValue);
    let rev;

    if (!revisions.length) {
      rev = await postPipingRev(projectIdValue, {
        name: "0",
        data: "{}",
      });
    } else {
      rev = revisions[revisions.length - 1];
    }

    setRevision(rev);
    const jsonData = await fetchPipingRev(projectIdValue, rev.id);
    parsePipingData(jsonData);
  }

  function parsePipingData(raw) {
    const data = raw?.data || {};

    setProjectPath(data.projectPath || null);
    setSelectedSpecs(data.selectedSpecs || []);
    // setAllCombinationTable(data.allCombinationTable || []);
    setTagNumbersList(migrateTagNumbersList(data.tagNumbersList));
    setSelectedLineNumber(String(data.selectedLineNumber ?? "").trim());
    setPdcAttachItems(data.pdcAttachItems || []);
    setEstimationTable(data.estimationTable || { 0: {} });
    // setUID_array(Array.isArray(data.UID_array) ? data.UID_array : []);
    setPartLibTable(() => {
      if (!data.partLibTable || Object.keys(data.partLibTable).length === 0) {
        return { 0: {} };
      }
      return data.partLibTable;
    });

    const normalizedPnidData = buildNormalizedPnidData(data);
    setPnidData(normalizedPnidData);

    const pnidRevisionKeys = Object.keys(normalizedPnidData)
      .filter((key) => !key.startsWith("__"))
      .sort((a, b) => Number(a) - Number(b));

    const savedPnidRevision = String(data.selectedPnidRevision || "").trim();
    const fallbackPnidRevision = pnidRevisionKeys.at(-1) || "";

    setSelectedPnidRevision(
      savedPnidRevision && normalizedPnidData[savedPnidRevision]
        ? savedPnidRevision
        : fallbackPnidRevision,
    );

    setPnidProjectPath(data.pnidProjectPath || "");
    setPnidRevisionFolders(data.pnidRevisionFolders || []);
    setPnidFolderTree(data.pnidFolderTree || null);

    const newModelingData = (() => {
      if (!data?.modelingData || Object.keys(data?.modelingData).length === 0) {
        if (
          !data?.modelingTable ||
          Object.keys(data?.modelingTable).length === 0
        ) {
          return { 0: {} };
        }
        return data.modelingTable;
      }
      return data.modelingData;
    })();

    setModelingData(newModelingData);
    setHookupsTable(data.hookupsTable || []);

    const validKeys = Object.keys(newModelingData)
      .filter((key) => !key.startsWith("__") && !Number.isNaN(Number(key)))
      .sort((a, b) => Number(a) - Number(b));

    const lastKey = validKeys.at(-1) ?? "0";
    setSelectedModelingRevision(lastKey);
  }
  function norm(value) {
    return String(value ?? "").trim();
  }

  function getPipeCombosForRow(row, allCombinationTable) {
    const spec = norm(row?.Spec);
    const size = norm(row?.Size);
    const sch = norm(row?.["Schedule/Rating"]);

    const source = Array.isArray(allCombinationTable)
      ? allCombinationTable
      : [];

    const combosMap = new Map();

    for (const r of source) {
      const rSpec = norm(r?.Spec);
      const rComp = norm(r?.Components ?? r?.Component);
      const rSize = norm(r?.Size);
      const rSch = norm(r?.["Schedule/Rating"]);
      const rEnds = norm(r?.Ends);
      const rMaterial = norm(r?.Material);

      if (rSpec !== spec) continue;
      if (rComp.toLowerCase() !== "pipe") continue;
      if (rSize !== size) continue;
      if (sch && rSch && rSch !== sch) continue;

      const key = `${rEnds}||${rMaterial}`;
      if (!combosMap.has(key)) {
        combosMap.set(key, {
          Ends: rEnds,
          Material: rMaterial,
        });
      }
    }

    return [...combosMap.values()];
  }

  function autoFillPipeRow(row, allCombinationTable) {
    if (norm(row?.Component).toLowerCase() !== "pipe") return row;

    const combos = getPipeCombosForRow(row, allCombinationTable);
    if (combos.length === 0) return row;

    const currentEnds = norm(row?.Ends);
    const currentMaterial = norm(row?.Material);

    const nextEnds = currentEnds || combos[0].Ends || "";
    const materialCandidates = combos
      .filter((c) => !nextEnds || c.Ends === nextEnds)
      .map((c) => c.Material)
      .filter(Boolean);

    const nextMaterial =
      currentMaterial || materialCandidates[0] || combos[0].Material || "";

    return {
      ...row,
      Ends: nextEnds,
      Material: nextMaterial,
    };
  }
  function buildPipingPayload() {
    const normalizedPnidData = Object.fromEntries(
      Object.entries(isPlainObject(pnidData) ? pnidData : {}).map(
        ([revisionKey, revisionValue]) => [
          String(revisionKey),
          normalizePnidRevisionData(revisionValue),
        ],
      ),
    );

    return {
      name: selectedProjectId,
      type: PIPING_TYPE,
      revision: revision?.id,
      data: {
        selectedSpecs,
        // specTable,
        partLibTable,
        SelectedParts,
        pdcAttachItems,
        modelingData,
        hookupsTable,
        projectPath,
        // allCombinationTable,
        tagNumbersList,
        selectedLineNumber,
        estimationTable,
        pnidData: normalizedPnidData,
        pnidProjectPath,
        pnidRevisionFolders,
        pnidFolderTree,
        selectedPnidRevision,
        UID_array,
      },
    };
  }

  async function savePipingData() {
    const data = buildPipingPayload();
    console.log("saving", data);

    if (revision?.id) {
      await updatePipingRev(selectedProjectId, revision.id, data);
    } else {
      const rev = await postPipingRev(selectedProjectId, {
        name: "0",
        data: data?.data || {},
      });
      setRevision(rev);
    }
  }


  // Load project data first
  useEffect(() => {
    if (!selectedProjectId) return;
    loadPipingData(selectedProjectId);
  }, [selectedProjectId]);
  function cleanLineValue(value) {
    return String(value ?? "").trim();
  }

  function getLineField(row, keys) {
    for (const key of keys) {
      const value = cleanLineValue(row?.[key]);
      if (value) return value;
    }
    return "";
  }

  function normalizeLinePipeSize(value) {
    return cleanLineValue(value).replace(/"/g, "");
  }

  function buildPipeBulkRowsFromLineData(lineRows, pnidPath) {
    const rows = Array.isArray(lineRows) ? lineRows : [];
    const seen = new Set();
    const out = [];

    for (const item of rows) {
      const lineNumber = getLineField(item, ["Line Number", "LineNumber"]);
      const spec = getLineField(item, [
        "Pipe Classification",
        "PipeClassification",
      ]);
      const size = normalizeLinePipeSize(
        getLineField(item, ["Nominal Pipe Size", "NominalPipeSize"]),
      );
      const scheduleRating = getLineField(item, [
        "Schedule/Rating",
        "ScheduleRating",
      ]);

      if (!spec || !size) continue;

      const dedupeKey = [pnidPath, lineNumber, spec, size, scheduleRating].join(
        "|",
      );

      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      out.push({
        _rowId: `line-pipe-${dedupeKey}`,
        _derived: true,
        _derivedFrom: "lineList",
        path: pnidPath,
        sourceLineNumber: lineNumber,
        "Line Number": lineNumber,
        Spec: spec,
        Component: "Pipe",
        Size: size,
        "Schedule/Rating": scheduleRating,
        Ends: "",
        Material: "",
        Qty: 1,
        Category: "Bulk",
      });

      out.push({
        _rowId: `line-elbow-${dedupeKey}`,
        _derived: true,
        _derivedFrom: "lineList",
        path: pnidPath,
        sourceLineNumber: lineNumber,
        "Line Number": lineNumber,
        Spec: spec,
        Component: "Elbow 90",
        Size: size,
        "Schedule/Rating": scheduleRating,
        Ends: "",
        Material: "",
        Qty: 1,
        Category: "Bulk",
      });
    }

    return out;
  }
  async function extractPnid(pnidPath) {
    if (!selectedPnidRevision) {
      console.warn("No selected P&ID revision");
      return;
    }

    setIsFetchingPnid(true);

    try {
      const { TaggedData, LineData } = await fetchExtractPnid(pnidPath);
      console.log("Fetched P&ID table:", TaggedData);

      const valveRowsRaw = Array.isArray(TaggedData.ValveData)
        ? TaggedData.ValveData
        : [];
      const instrumentRowsRaw = Array.isArray(TaggedData.InstrumentData)
        ? TaggedData.InstrumentData
        : [];

      const valveRowsWithSheets = valveRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "valve", allTables) ??
          r?.dataSheet ??
          null,
      }));
      const instrumentRowsWithSheets = instrumentRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "instrument", allTables) ??
          r?.dataSheet ??
          null,
      }));

      const equipmentRowsRaw = Array.isArray(TaggedData.EquipmentData)
        ? TaggedData.EquipmentData
        : [];
      const equipmentRowsWithSheets = equipmentRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "equipment", allTables) ??
          r?.dataSheet ??
          null,
      }));

      setInstrumentIndexTable(instrumentRowsWithSheets);
      setValveListTable(valveRowsWithSheets);

      const extractedLineRows = Array.isArray(LineData?.LineData)
        ? LineData.LineData
        : [];


      const rawBulkRows = Array.isArray(TaggedData?.BulkData)
        ? TaggedData.BulkData
        : [];

      // Pipe and Elbow should come only from line list
      const bulkWithoutPipeAndElbow = rawBulkRows.filter((row) => {
        const comp = String(row?.Component || "").trim().toLowerCase();
        return comp !== "pipe" && comp !== "elbow" && comp !== "elbow 90";
      });

      const lineDerivedRows = buildPipeBulkRowsFromLineData(
        extractedLineRows,
        pnidPath,
      );

      setBulkListTable((prev) => {
        const prevRows = Array.isArray(prev) ? prev : [];

        // remove old Pipe / Elbow rows for the same drawing path
        const cleanedPrev = prevRows.filter((row) => {
          const samePath = String(row?.path || "") === String(pnidPath);
          const comp = String(row?.Component || "").trim().toLowerCase();
          const isPipeOrElbow =
            comp === "pipe" || comp === "elbow" || comp === "elbow 90";

          return !(samePath && isPipeOrElbow);
        });

        // normal bulk rows still use derivation helper
        const mergedBase = mergeBulkDataWithDerivation(
          cleanedPrev,
          bulkWithoutPipeAndElbow,
          allTables,
        );

        // line-derived Pipe / Elbow rows merge directly
        return mergeBulkData(mergedBase, lineDerivedRows);
      });


      setTitleBlockData(TaggedData.TitleBlock || {});

      const trimRowsWithSheets = (TaggedData.Trimlist || []).map((r) => ({
        ...r,
        dataSheet:
          buildRowDataSheet(
            r,
            valveRowsWithSheets,
            instrumentRowsWithSheets,
            allTables,
            equipmentRowsWithSheets,
          ) ??
          r?.dataSheet ??
          null,
      }));
      setTrimListTable((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...trimRowsWithSheets,
      ]);

      setEquipmentListTable((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...equipmentRowsWithSheets,
      ]);

      const mergeConsolidated = (prev, next) => {
        const prevItems = Array.isArray(prev?.items) ? prev.items : [];
        const nextItems = Array.isArray(next?.items) ? next.items : [];
        const items = [...prevItems, ...nextItems];
        return {
          tab: next?.tab ?? prev?.tab,
          totalSourceRows:
            (prev?.totalSourceRows || 0) + (next?.totalSourceRows || 0),
          totalGroupedSheets: items.length,
          items,
        };
      };

      setDataSheetsStore((prev) => ({
        ...prev,
        valve: mergeConsolidated(
          prev?.valve,
          consolidateDataSheets(valveRowsWithSheets, "valve", allTables),
        ),
        instrument: mergeConsolidated(
          prev?.instrument,
          consolidateDataSheets(
            instrumentRowsWithSheets,
            "instrument",
            allTables,
          ),
        ),
        equipment: mergeConsolidated(
          prev?.equipment,
          consolidateDataSheets(
            equipmentRowsWithSheets,
            "equipment",
            allTables,
          ),
        ),
      }));

      if (LineData && LineData.LineData && Array.isArray(LineData.LineData)) {
        const mappedLineData = LineData.LineData.map((item) => ({
          path: pnidPath,
          ...item,
        }));
        setLineListTable((prev) => {
          const prevRows = Array.isArray(prev) ? prev : [];
          const cleanedPrev = prevRows.filter(
            (row) => String(row?.path || "") !== String(pnidPath),
          );
          return [...cleanedPrev, ...mappedLineData];
        });
        // setLineListTable((prev) => [...prev, ...mappedLineData]);
        // console.log("Mapped Line Data:", mappedLineData);
      }
    } catch (e) {
      console.log("Error while fetching pnid: ", e);
    } finally {
      setIsFetchingPnid(false);
    }
  }

  return (
    <PipingContext.Provider
      value={{
        selectedProjectId,
        setSelectedProjectId,
        specTable,
        fullSpecTable,
        allTables,
        initializeAllTables,
        selectedSpecs,
        setSelectedSpecs,
        SelectedParts,
        addSlectedSpec,
        removeSelectedSpec,
        getSelectedSpecTable,
        savePipingData,
        partLibTable,
        setPartLibTable,
        estimationTable,
        setEstimationTable,
        pnidData,
        setPnidData,
        selectedPnidRevision,
        setSelectedPnidRevision,
        pnidProjectPath,
        setPnidProjectPath,
        pnidRevisionFolders,
        setPnidRevisionFolders,
        pnidFolderTree,
        setPnidFolderTree,
        initializeRevisionIfNeeded,
        clearSelectedPnidRevision,
        trimListTable,
        setTrimListTable,
        bulkListTable,
        setBulkListTable,
        valveListTable,
        setValveListTable,
        instrumentIndexTable,
        setInstrumentIndexTable,
        lineListTable,
        setLineListTable,
        equipmentListTable,
        setEquipmentListTable,
        dataSheetsStore,
        setDataSheetsStore,
        titleBlockData,
        setTitleBlockData,
        extractPnid,
        isFetchingPnid,
        pnidDrawings,
        setPnidDrawings,
        modelingData,
        setModelingData,
        pdcAttachItems,
        setPdcAttachItems,
        allUiData,
        pageViews,
        setPageViews,
        pages,
        setPages,
        selectedModelingRevision,
        setSelectedModelingRevision,
        projectPath,
        setProjectPath,
        hookupsTable,
        setHookupsTable,
        getRevisionsFromPath,
        fastenersTable,
        setFastenersTable,
        totalSummary,
        setTotalSummary,
        tagNumbersList,
        setTagNumbersList,
        selectedLineNumber,
        setSelectedLineNumber,
        allCombinationTable,
        setAllCombinationTable,
        loadPipingData,
        validationObject,
        setValidationObject,
        clientName,
        UID_array,
        setUID_array,
      }}
    >
      {children}
    </PipingContext.Provider>
  );
};

export const usePiping = () => {
  const context = useContext(PipingContext);
  if (!context) {
    throw new Error("usePiping must be used within a PipingProvider");
  }
  return context;
};
